import { useMemo, useState } from "react";
import {
  fuelLogsForCar, newId, catalogFiltered, intervalLabel, reminderTypeForProcedure,
  reminderStatus, reminderSubtitle, reminderTypeName,
  type CanonicalStore, type Car, type MaintenanceReminder, type CatalogProcedure, type ReminderStatus,
} from "../../core/index.ts";
import { BackRow } from "../forms/Modal.tsx";
import { epochToDateInput, dateInputToEpoch } from "../format.ts";

const STATUS_COLOR: Record<ReminderStatus, string> = {
  overdue: "var(--redline)", due_soon: "var(--amber)", on_track: "var(--lime)", completed: "var(--muted)",
};
const STATUS_LABEL: Record<ReminderStatus, string> = {
  overdue: "OVERDUE", due_soon: "DUE SOON", on_track: "ON TRACK", completed: "DONE",
};
const TYPES = [
  { raw: "oil_change", name: "Oil Change" }, { raw: "tyre_change", name: "Tire Change" },
  { raw: "service_appointment", name: "Service Appointment" }, { raw: "custom", name: "Custom" },
];

const inp: React.CSSProperties = {
  width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10,
  color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 13, padding: "10px 14px", boxSizing: "border-box",
};

interface Draft {
  id: string; existing: boolean; title: string; type: string;
  dueDate: string; dueOdometer: string; notes: string;
  recursEveryDays: string; recursEveryDistance: string; isCompleted: boolean;
}

export function MaintenanceView({ store, car, save, isPro, requirePro, onBack }: {
  store: CanonicalStore; car: Car; save: (s: CanonicalStore) => void; isPro: boolean; requirePro: () => void; onBack: () => void;
}) {
  const reminders = store.maintenanceReminders.filter((r) => r.carId === car.id);
  const currentOdo = useMemo(() => fuelLogsForCar(store.fuelLogs, car.id).reduce((m, l) => Math.max(m, l.odometer), 0), [store.fuelLogs, car.id]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [catalog, setCatalog] = useState(false);

  const commit = (list: MaintenanceReminder[]) => save({ ...store, maintenanceReminders: list });

  function saveDraft(d: Draft) {
    const r: MaintenanceReminder = {
      id: d.id, carId: car.id, title: d.title.trim() || "Reminder", reminderType: d.type,
      dueDate: d.dueDate ? dateInputToEpoch(d.dueDate) : null,
      dueOdometer: d.dueOdometer ? Number(d.dueOdometer) : null,
      notes: d.notes.trim() || null, isCompleted: d.isCompleted,
      recursEveryDays: d.recursEveryDays ? Number(d.recursEveryDays) : null,
      recursEveryDistance: d.recursEveryDistance ? Number(d.recursEveryDistance) : null,
      createdAt: store.maintenanceReminders.find((x) => x.id === d.id)?.createdAt ?? Math.floor(Date.now() / 1000),
    };
    const others = store.maintenanceReminders.filter((x) => x.id !== d.id);
    commit([...others, r]);
    setDraft(null);
  }

  const blank = (): Draft => ({ id: newId(), existing: false, title: "", type: "custom", dueDate: "", dueOdometer: "", notes: "", recursEveryDays: "", recursEveryDistance: "", isCompleted: false });

  function fromProcedure(p: CatalogProcedure): Draft {
    const now = Math.floor(Date.now() / 1000);
    return {
      id: newId(), existing: false, title: p.nameEn, type: reminderTypeForProcedure(p),
      dueDate: p.defaultIntervalDays != null ? epochToDateInput(now + p.defaultIntervalDays * 86400) : "",
      dueOdometer: p.defaultIntervalKm != null ? String(Math.round(currentOdo + p.defaultIntervalKm)) : "",
      notes: "", recursEveryDays: p.defaultIntervalDays != null ? String(p.defaultIntervalDays) : "",
      recursEveryDistance: p.defaultIntervalKm != null ? String(p.defaultIntervalKm) : "", isCompleted: false,
    };
  }
  function edit(r: MaintenanceReminder): Draft {
    return {
      id: r.id, existing: true, title: r.title ?? "", type: r.reminderType ?? "custom",
      dueDate: epochToDateInput(r.dueDate ?? null), dueOdometer: r.dueOdometer != null ? String(r.dueOdometer) : "",
      notes: r.notes ?? "", recursEveryDays: r.recursEveryDays != null ? String(r.recursEveryDays) : "",
      recursEveryDistance: r.recursEveryDistance != null ? String(r.recursEveryDistance) : "", isCompleted: r.isCompleted ?? false,
    };
  }

  const gate = (fn: () => void) => (isPro ? fn() : requirePro());

  if (catalog) return <CatalogPicker vehicleType={car.vehicleType} metric={store.profile.unitSystem === "metric"} onPick={(p) => { setCatalog(false); setDraft(fromProcedure(p)); }} onClose={() => setCatalog(false)} />;
  if (draft) return <Editor draft={draft} onChange={setDraft} onSave={() => saveDraft(draft)} onDelete={draft.existing ? () => { commit(store.maintenanceReminders.filter((x) => x.id !== draft.id)); setDraft(null); } : undefined} onClose={() => setDraft(null)} />;

  const sorted = [...reminders].sort((a, b) => {
    const order: Record<ReminderStatus, number> = { overdue: 0, due_soon: 1, on_track: 2, completed: 3 };
    return order[reminderStatus(a, currentOdo)] - order[reminderStatus(b, currentOdo)] || (a.dueDate ?? 0) - (b.dueDate ?? 0);
  });

  return (
    <div className="container glow">
      <BackRow onClick={onBack} />
      <div className="eyebrow amber" style={{ marginTop: 4 }}>MAINTENANCE · <span className="muted">{car.model}</span></div>
      <div className="headline">Service <span className="accent">plan.</span></div>

      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button className="btn primary" style={{ flex: 1, fontSize: 11 }} onClick={() => gate(() => setCatalog(true))}>+ FROM CATALOG</button>
        <button className="btn ghost" style={{ flex: 1, fontSize: 11 }} onClick={() => gate(() => setDraft(blank()))}>+ CUSTOM</button>
      </div>

      {sorted.length === 0 && <div className="panel mono muted" style={{ fontSize: 12, marginTop: 12 }}>No reminders yet. Add one from the catalog or a custom entry.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
        {sorted.map((r) => {
          const st = reminderStatus(r, currentOdo);
          return (
            <div key={r.id} className="panel" style={{ cursor: "pointer" }} onClick={() => gate(() => setDraft(edit(r)))}>
              <div className="mono cream" style={{ fontSize: 13, fontWeight: 600, textDecoration: r.isCompleted ? "line-through" : "none" }}>{r.title}</div>
              {/* Status on its own line under the name rather than opposite it: a
                  long title used to squeeze the badge, and the eye now reads
                  name -> status -> schedule straight down. */}
              <div style={{ marginTop: 5 }}>
                <span className="mono" style={{ fontSize: 8, letterSpacing: 1.2, padding: "3px 8px", borderRadius: 4, color: STATUS_COLOR[st], border: `1px solid ${STATUS_COLOR[st]}` }}>{STATUS_LABEL[st]}</span>
              </div>
              <div className="mono muted" style={{ fontSize: 10, marginTop: 4 }}>{reminderTypeName(r.reminderType)} · {reminderSubtitle(r, currentOdo)}</div>
            </div>
          );
        })}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}

function Editor({ draft, onChange, onSave, onDelete, onClose }: {
  draft: Draft; onChange: (d: Draft) => void; onSave: () => void; onDelete?: () => void; onClose: () => void;
}) {
  const set = (patch: Partial<Draft>) => onChange({ ...draft, ...patch });
  return (
    <div className="container glow">
      <BackRow onClick={onClose} />
      <div className="screen-title">{draft.existing ? "EDIT" : "NEW"} REMINDER</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>TITLE</label>
        <input style={inp} value={draft.title} placeholder="e.g. Oil Change" onChange={(e) => set({ title: e.target.value })} />
        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>TYPE</label>
        <select style={inp} value={draft.type} onChange={(e) => set({ type: e.target.value })}>
          {TYPES.map((t) => <option key={t.raw} value={t.raw}>{t.name}</option>)}
        </select>
        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>DUE DATE</label>
        <input style={inp} type="date" value={draft.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>DUE ODOMETER (KM)</label>
        <input style={inp} type="number" value={draft.dueOdometer} placeholder="0" onChange={(e) => set({ dueOdometer: e.target.value })} />
        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>REPEAT EVERY (DAYS)</label>
        <input style={inp} type="number" value={draft.recursEveryDays} placeholder="—" onChange={(e) => set({ recursEveryDays: e.target.value })} />
        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>REPEAT EVERY (KM)</label>
        <input style={inp} type="number" value={draft.recursEveryDistance} placeholder="—" onChange={(e) => set({ recursEveryDistance: e.target.value })} />
        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>NOTES</label>
        <input style={inp} value={draft.notes} placeholder="Notes" onChange={(e) => set({ notes: e.target.value })} />
        <label className="mono" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 8, color: "var(--cream)", cursor: "pointer" }}>
          <input type="checkbox" checked={draft.isCompleted} onChange={(e) => set({ isCompleted: e.target.checked })} /> Mark completed
        </label>
        <button className="btn primary" style={{ marginTop: 6 }} onClick={onSave}>SAVE</button>
        {onDelete && <button className="btn ghost" style={{ borderColor: "var(--redline)", color: "var(--redline)" }} onClick={onDelete}>DELETE</button>}
      </div>
    </div>
  );
}

function CatalogPicker({ vehicleType, metric, onPick, onClose }: {
  vehicleType?: string; metric: boolean; onPick: (p: CatalogProcedure) => void; onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const results = useMemo(() => catalogFiltered(q, vehicleType), [q, vehicleType]);
  const groups = useMemo(() => {
    const m = new Map<string, CatalogProcedure[]>();
    for (const p of results) { const g = m.get(p.category) ?? []; g.push(p); m.set(p.category, g); }
    return [...m.entries()];
  }, [results]);

  return (
    <div className="container glow">
      <BackRow onClick={onClose} />
      <div className="screen-title">PROCEDURE CATALOG</div>
      <input style={{ ...inp, marginTop: 8 }} value={q} placeholder="Search procedures…" onChange={(e) => setQ(e.target.value)} />
      <div style={{ marginTop: 10 }}>
        {groups.map(([cat, items]) => (
          <div key={cat}>
            <div className="mono amber" style={{ fontSize: 10, letterSpacing: 2.4, marginTop: 16, marginBottom: 8 }}>{cat.toUpperCase()}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map((p) => (
                <button key={p.key} className="panel" style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left", cursor: "pointer", width: "100%" }} onClick={() => onPick(p)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mono cream" style={{ fontSize: 13, fontWeight: 600 }}>{p.nameEn}</div>
                    <div className="mono muted" style={{ fontSize: 10, marginTop: 2 }}>{intervalLabel(p, metric)}</div>
                  </div>
                  <span className="mono amber" style={{ fontSize: 14 }}>+</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
